import { Link } from 'react-router-dom'
import { StatusBadge, LatencyBadge } from './StatusBadge'
import { SearchInput } from './SearchInput'
import { ArrowRight, Monitor, Copy, Check } from 'lucide-react'
import type { Device } from '../types'
import { useMemo, useState, useCallback } from 'react'
import { formatDistanceToNow } from '../utils/date'

interface DeviceTableProps {
  devices: Device[]
  isLoading?: boolean
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }, [text])

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-surface-elevated transition-colors flex-shrink-0"
      title={copied ? 'Copied!' : `Copy ${text}`}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-success" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-text-muted hover:text-text" />
      )}
    </button>
  )
}

export function DeviceTable({ devices, isLoading = false }: DeviceTableProps) {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return devices
    return devices.filter(d =>
      d.id.toLowerCase().includes(term) ||
      d.name?.toLowerCase().includes(term) ||
      d.zt_ip?.toLowerCase().includes(term) ||
      d.version?.toLowerCase().includes(term)
    )
  }, [devices, search])

  if (isLoading && devices.length === 0) {
    return <DeviceTableSkeleton />
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="max-w-md w-full">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by ID, name, IP, version..."
          />
        </div>
        <div className="text-sm text-text-muted">
          {filtered.length} device{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Device</th>
              <th className="hidden md:table-cell">ZT IP</th>
              <th>Status</th>
              <th className="hidden sm:table-cell">Last Seen</th>
              <th className="hidden xl:table-cell">Latency</th>
              <th className="hidden xl:table-cell">Version</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(device => (
              <tr key={device.id}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                      <Monitor className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {device.name || device.id}
                        <CopyButton text={device.name || device.id} />
                      </div>
                      <div className="text-xs text-text-muted font-mono truncate">
                        {device.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="hidden md:table-cell font-mono text-sm">
                  <div className="flex items-center gap-1.5">
                    {device.zt_ip || <span className="text-text-muted">—</span>}
                    {device.zt_ip && <CopyButton text={device.zt_ip} />}
                  </div>
                </td>
                <td>
                  <StatusBadge online={device.online} pulse={device.online} />
                </td>
                <td className="hidden sm:table-cell text-sm text-text-muted">
                  {device.last_seen
                    ? formatDistanceToNow(device.last_seen)
                    : <span>—</span>}
                </td>
                <td className="hidden xl:table-cell">
                  <LatencyBadge latency={device.latency} />
                </td>
                <td className="hidden xl:table-cell text-sm">
                  {device.version || <span className="text-text-muted">—</span>}
                </td>
                <td className="text-right">
                  <Link
                    to={`/devices/${device.id}`}
                    className="btn btn-secondary text-sm inline-flex"
                  >
                    Details
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12 text-text-muted">
                  No devices found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DeviceTableSkeleton() {
  return (
    <div className="space-y-4">
      <div className="skeleton h-10 w-full max-w-md rounded-md" />
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {Array.from({ length: 7 }).map((_, i) => (
                <th key={i}><div className="skeleton h-4 w-16" /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {Array.from({ length: 7 }).map((_, j) => (
                  <td key={j}><div className="skeleton h-4 w-full" /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
